import { Box, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { Shareholder, ShareClass } from "../../types/Shareholders";
import { TEXT_SECONDARY } from "../colors";

const TOP_N = 8;
const NUMBER_FORMAT = new Intl.NumberFormat("en-GB");

// Dark-to-light brand purple gradient, one shade per slice. Every slice gets
// an explicit color here - leaving any of them undefined and relying on a
// shorter default/custom palette to fill the gap is what produced an
// unstyled (effectively black) slice with no color at all in the tooltip.
const gradientColor = (index: number, count: number) => {
  const dark: [number, number, number] = [42, 20, 48]; // DELTA_PRIMARY_DARK
  const light: [number, number, number] = [181, 101, 186]; // DELTA_ACCENT_LIGHT
  const t = count <= 1 ? 0 : index / (count - 1);
  const [r, g, b] = dark.map((c, i) => Math.round(c + (light[i] - c) * t));
  return `rgb(${r}, ${g}, ${b})`;
};

// A pie chart blending different share classes together wouldn't mean much -
// classes aren't necessarily fungible 1:1 (different rights/value) - so this
// charts only the largest class by total shares, which covers the only class
// that exists for the vast majority of companies anyway.
export const ShareholdersChart = (props: { shareholders: Shareholder[]; classes: ShareClass[] }) => {
  const { shareholders, classes } = props;

  if (classes.length === 0) {
    return null;
  }

  const largestClass = [...classes].sort((a, b) => b.total_shares - a.total_shares)[0];
  const inClass = [...shareholders]
    .filter((s) => s.class === largestClass.class)
    .sort((a, b) => b.shares - a.shares);

  const top = inClass.slice(0, TOP_N);
  const rest = inClass.slice(TOP_N);
  const otherShares = rest.reduce((sum, s) => sum + s.shares, 0);
  const chartTotal = top.reduce((sum, s) => sum + s.shares, 0) + otherShares;

  const data = [
    ...top.map((s, index) => ({
      id: index,
      value: s.shares,
      label: s.name,
      color: gradientColor(index, top.length),
    })),
    ...(otherShares > 0
      ? [{ id: -1, value: otherShares, label: `Other (${rest.length})`, color: TEXT_SECONDARY }]
      : []),
  ];

  return (
    <Box style={{ padding: 10 }}>
      <Typography variant="subtitle2" style={{ color: TEXT_SECONDARY, marginBottom: 8 }}>
        {classes.length > 1 ? `${largestClass.class} Shareholders` : "Shareholders"}
      </Typography>
      <Box style={{ height: 320, display: "flex", justifyContent: "center" }}>
        <PieChart
          series={[
            {
              data,
              innerRadius: 40,
              paddingAngle: 1,
              cornerRadius: 2,
              valueFormatter: (item) => {
                const percent = chartTotal > 0 ? (item.value / chartTotal) * 100 : 0;
                return `${NUMBER_FORMAT.format(item.value)} shares (${percent.toFixed(2)}%)`;
              },
            },
          ]}
          height={320}
          slotProps={{ legend: { hidden: true } }}
        />
      </Box>
    </Box>
  );
};

export default ShareholdersChart;
